import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  HoldingResult,
  JacketArt,
  ShelfStatusModel,
  VersoHolding,
} from '../../core/interfaces/search.interface';
import { Definitions } from '../../core/definitions';
import { ConfigService } from '../config/config.service';
import { StorageService } from '../storage/storage.service';
import { LocationNode } from '../library/library.service';
import { ResponseModel } from '../api-response.interface';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '../apiservice/apiservice.service';
import { Format } from '../../core/interfaces/format.interface';

// SearchAPI Holdings
export interface IVersoHoldingRequestItem {
  libraryDBPoolKey: number;
  agControlIds: string;
}

export interface IVersoHoldingRequest {
  searchId: string;
  versoHoldingRequestItems: IVersoHoldingRequestItem[];
}

// AGAPI Holdings
export interface HoldingsStatusData {
  libraryId: string;
  status: string;
}

export interface HoldingPageData {
  bibMetadata: BibMetadataModel;
  holding: HoldingModel;
  itemCategories: ItemCategory[];
  locations: LocationNode[];
  materialTypes: MaterialType[];
  holdingStatuses: HoldingStatus[];
  autoGeneratedBarCode: boolean;
}

export interface ItemCategory {
  id: number;
  name: string;
}

export interface MaterialType {
  id: number;
  name: string;
  active: boolean;
  suppress: boolean;
  hide: boolean;
}

export interface HoldingStatus {
  displayName: string;
  colorCode: string;
  code: string;
}

export interface ReportHoldingStatus {
  code: string;
  label: string;
}

export class AVBibHolding {
  holdingId: number;
  libraryCode: string;
  lpk: number;
  agControlId: number;
  barcode: string;
  prefix: string;
  suffix: string;
  callNumber: string;
  enum: string;
  chron: string;
  loc: number;
  locP: number;
  loc2: number;
  loc3: number;
  mtypeId: number;
  dd: string;
  copId: number;
  iStat: string;
  icnSort: string;
  chrSort: string;
  enumSort: string;
  altId: string;
  copy: string;
  cost: number;
  comment: string;
  note: string;
  volume: string;
  issue: string;
  part: string;
  sup: string;
  other: string;
  year: string;
  month: string;
  day: string;
  season: string;
  nonCirc: boolean;
}

export class HoldingModel extends AVBibHolding {
  itemCategoryIds: number[];
}

export class AVBibHoldingManagement extends AVBibHolding {
  LocName: string;
  LocName2: string;
  LocName3: string;
  LocPermanentName: string;
  MType: string;
  Status: string;
  CanEditHolding: boolean;
  LocDescription: string;
  Loc2Description: string;
  Loc3Description: string;
}

export class BibMetadataModel {
  agControlId: string;
  jacketArt: JacketArt;
  title: string;
  author: string;
  publisher: string;
  year: number;
  sourceKey: string;
  libraryDBPoolKey: number;
  isbn: string[];
  issn: string[];
  format: Format;
}

export interface BibHoldingInfo {
  holdingId: number;
  libCode: string;
  barcode: string;
}

export class ManageHoldingsModel {
  items: AVBibHoldingManagement[];
  bibMetadata: BibMetadataModel;
  canAddItems: boolean;
  canEditBib: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class HoldingsService {
  readonly UNABLE_TO_FETCH_SHELF_STATUS = 'Shelf Status is not available.';
  private _configService = inject(ConfigService);
  private _storageService = inject(StorageService);
  private _http = inject(HttpClient);
  private _apiService = inject(ApiService);
  private _holdingUrl = `${this._configService.searchApi}/holding`;
  private _itemUrl = `${this._configService.agApi}/item`;

  // SearchAPI Holdings
  getVersoHoldings(payload): Observable<VersoHolding[]> {
    const url = `${this._holdingUrl}/Verso`;
    return this._http.post(url, payload).pipe(
      map((response: ResponseModel) => {
        if (response.data[0]) {
          this.setCachedHoldings(response.data[0]);
          return response.data[0];
        } else {
          throw new Error('Unable to fetch verso holdings.');
        }
      }),
    );
  }

  getHoldings(data): Observable<HoldingResult> {
    // TODO: replaces "getHoldingsAsync"
    const url = `${this._holdingUrl}`;
    return this._http.post(url, data).pipe(
      map((response: ResponseModel) => {
        if (response.data) {
          this.setCachedHoldings(response.data);
          return response.data;
        } else {
          throw new Error('Unable to fetch holdings.');
        }
      }),
    );
  }

  // AGAPI Holding Management
  getVersoHoldingsManagementPage(agcn: number): Observable<ManageHoldingsModel> {
    const url = `${this._itemUrl}/manage`;
    return this._http
      .get(url, {
        params: {
          agControlId: agcn,
        },
      })
      .pipe(
        map((response: ManageHoldingsModel) => {
          if (!response) {
            throw new Error('Unable to fetch holdings.');
          }
          return response;
        }),
      );
  }

  getNewVersoHoldingPage(agcn: number): Observable<HoldingPageData> {
    const url = `${this._itemUrl}/new`;
    return this._http
      .get(url, {
        params: {
          agControlId: agcn,
        },
      })
      .pipe(
        map((response: HoldingPageData) => {
          if (!response) {
            throw new Error('Unable to fetch holding.');
          }
          return response;
        }),
      );
  }

  getVersoHoldingPage(holdingId: string): Observable<HoldingPageData> {
    const url = `${this._itemUrl}/${holdingId}`;
    return this._http.get(url).pipe(
      map((response: HoldingPageData) => {
        if (!response) {
          throw new Error('Unable to fetch holding.');
        }
        return response;
      }),
    );
  }

  saveVersoHolding(data: HoldingModel): Observable<HoldingModel> {
    return this._http.post(this._itemUrl, data).pipe(
      map((response: HoldingModel) => {
        if (!response) {
          throw new Error('Unable to save holding.');
        }

        return response;
      }),
    );
  }

  checkForDuplicateBarcode(barcode: string, holdingId: number): Observable<boolean> {
    const url = `${this._itemUrl}/duplicateCheck`;

    return this._http.post(url, { barcode: barcode, holdingId: holdingId }).pipe(
      map((response: boolean) => {
        return response;
      }),
    );
  }

  getShareItHoldings(holdingids, maxNumberOfHoldings = 0): Observable<any> {
    const payload = { holdingids };
    if (maxNumberOfHoldings > 0) {
      // default 5 holdings set in searchapi
      payload['maxNumberOfHoldings'] = maxNumberOfHoldings;
    }
    return this.getHoldings(payload);
  }

  showOwnedByMyLibraryMessage(): Observable<boolean> {
    const url = `${this._holdingUrl}/ShowOwnedByMyLibraryMessage`;
    return this._apiService.get(url).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  setCachedHoldings(value) {
    this._storageService.setItem(Definitions.holdingsSessionKey, value);
  }

  getCachedHoldings() {
    return this._storageService.getItem(Definitions.holdingsSessionKey);
  }

  getShelfStatus(shelfData: string): Observable<Map<string, string>> {
    const payload: ShelfStatusModel = JSON.parse(shelfData);
    const url = `${this._holdingUrl}/CheckShelfStatus`;
    return this._http.post(url, payload).pipe(
      catchError((err: unknown) => {
        console.error(err);
        throw new Error(this.UNABLE_TO_FETCH_SHELF_STATUS);
      }),
      map((response: ResponseModel) => {
        if (response?.status == 'OK' && response?.data?.data) {
          const statusResponse = new Map<string, string>();
          for (const shelfStat of response.data.data) {
            statusResponse.set(shelfStat.libraryCode, shelfStat.status);
          }
          return statusResponse;
        } else {
          console.error(response.message);
          throw new Error(this.UNABLE_TO_FETCH_SHELF_STATUS);
        }
      }),
    );
  }

  getHoldingStatuses(lpk: number): Observable<ReportHoldingStatus[]> {
    const url = `${this._itemUrl}/statuses`;
    return this._http
      .get<ReportHoldingStatus[]>(url, {
        params: {
          lpk: lpk,
        },
      })
      .pipe(
        map((response: ReportHoldingStatus[]) => {
          if (!response) {
            throw new Error('Unable to fetch holding statuses.');
          }
          return response;
        }),
        catchError((error: unknown) => this._handleError(error)),
      );
  }

  deleteHolding(holdingId: number, dryRun: boolean = true) {
    const url = `${this._itemUrl}/${holdingId}`;
    const params = { dryRun: dryRun };
    return this._apiService
      .delete(url, { params: params })
      .pipe(catchError((error: unknown) => this._handleError(error)));
  }

  private _handleError(error: unknown): Observable<never> {
    console.error('An error occurred:', error);
    return throwError(error);
  }
}
